import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleLeft, faAngleRight, faSpinner, faPlus, faTrash } from '@fortawesome/free-solid-svg-icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import PageHeadingSection from '../../../../common/pageheading/PageHeadingSection';
import apiServices from '../../../../../services/apiService';
import CustomSelect from '../../../../common/formfields/CustomSelect';
import { primaryColor, secondaryColor } from '../../../../../utils/comon';
import Breadcrumbs from '../../../../common/BreadCrum/BreadCrum';

const UnitForm = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [isLoading, setIsLoading] = useState(false);
    const [isFetching, setIsFetching] = useState(false);

    const [sites, setSites] = useState([]);
    const [wings, setWings] = useState([]);
    const [floors, setFloors] = useState([]);
    const [unitNumbers, setUnitNumbers] = useState([]);

    const [formData, setFormData] = useState({
        siteId: null,
        unitName: '',
        isActive: 'Active',
        units: [],
        isStaffApprovalRequired: false
    });

    const [currentUnit, setCurrentUnit] = useState({
        wingId: null,
        floorId: null,
        unitNumberIds: []
    });

    const isEdit = !!location.state?.id;
    const editId = location.state?.id;

    useEffect(() => {
        const fetchMasterData = async () => {
            try {
                // Fetch Sites
                const siteRes = await apiServices({ method: 'GET', url: '/SiteDetailsMaster/getAllSiteDetails' });
                if (siteRes.success) {
                    let siteData = siteRes.data?.Data || [];
                    setSites(siteData.map(s => ({
                        label: s.SiteName,
                        value: s.Id
                    })));
                }

                // Fetch Wings
                const wingRes = await apiServices({ method: 'GET', url: '/WingMaster/GetAllWing' });
                if (wingRes.success) {
                    const wingData = wingRes.data?.WingMasterData || [];
                    setWings(wingData);
                }

                // Fetch Floors
                const floorRes = await apiServices({ method: 'GET', url: '/FloorMaster/GetAllFloor' });
                if (floorRes.success) {
                    const floorData = floorRes.data?.FloorMasterData || [];
                    setFloors(floorData);
                }

                // Fetch Unit Numbers
                const unitNumbersRes = await apiServices({
                    method: 'GET',
                    url: `/UnitNumberMaster/GetAllUnitNumberMaster?siteId=${localStorage.getItem('siteId') || 2}&pageNumber=1&pageSize=10000`
                });
                if (unitNumbersRes.success || unitNumbersRes.IsSuccess) {
                    let apiData = unitNumbersRes.data || unitNumbersRes.Data;
                    let dataArray = [];
                    if (Array.isArray(apiData)) dataArray = apiData;
                    else if (apiData && Array.isArray(apiData.data)) dataArray = apiData.data;
                    else if (apiData && Array.isArray(apiData.UnitMasterData)) dataArray = apiData.UnitMasterData;

                    setUnitNumbers(dataArray);
                }

                // If edit mode, fetch unit details
                if (isEdit) {
                    setIsFetching(true);
                    const unitRes = await apiServices({ method: 'GET', url: `/UnitMaster/GetUnitMasterById/${editId}` });

                    if (unitRes.success) {
                        let apiData = unitRes?.data?.data[0];

                        // Based on the edit API response shape (either array of units or flattened),
                        // we try to map the units gracefully. If it's flattened, we just seed one unit.
                        const parsedUnits = Array.isArray(apiData.units) && apiData.units.length > 0
                            ? apiData.units.map(u => ({
                                wingId: u.WingId || u.wingId,
                                floorId: u.FloorId || u.floorId,
                                unitNumberId: u.UnitNumberId || u.unitNumberId || null,
                                unitNumber: u.UnitNumber || u.unitNumber || ''
                            }))
                            : (apiData.WingId && apiData.FloorId ? [{
                                wingId: apiData.WingId,
                                floorId: apiData.FloorId,
                                unitNumberId: apiData.UnitNumberId || null,
                                unitNumber: apiData.UnitNumber || ''
                            }] : []);

                        setFormData({
                            siteId: apiData.SiteId,
                            unitName: apiData.UnitName || '',
                            isActive: apiData.IsActive || 'Active',
                            units: parsedUnits,
                            isStaffApprovalRequired: apiData.IsStaffApprovalRequired || false
                        });
                    }
                }
            } catch (error) {
                console.error("Error fetching data:", error);
                toast.error("Failed to load necessary data.");
            } finally {
                setIsFetching(false);
            }
        };

        fetchMasterData();
    }, [isEdit, editId]);

    const handleInputChange = (e) => {
        const { id, value } = e.target;
        setFormData(prev => ({ ...prev, [id]: value }));
    };

    const handleSelectChange = (key, option) => {
        setFormData(prev => {
            const next = { ...prev, [key]: option ? option.value : null };
            if (key === 'siteId') {
                next.units = [];
            }
            return next;
        });
    };

    const handleCurrentUnitChange = (key, option) => {
        setCurrentUnit(prev => {
            const next = { ...prev, [key]: option ? (Array.isArray(option) ? option : option.value) : (Array.isArray(prev[key]) ? [] : null) };
            if (key === 'wingId') {
                next.floorId = null;
                next.unitNumberIds = [];
            }
            if (key === 'floorId') {
                next.unitNumberIds = [];
            }
            return next;
        });
    };

    const handleAddUnitToList = () => {
        if (!currentUnit.wingId || !currentUnit.floorId || currentUnit.unitNumberIds.length === 0) {
            toast.warning("Please select Wing, Floor, and at least one Unit Number.");
            return;
        }

        const newUnits = currentUnit.unitNumberIds.map(opt => ({
            wingId: currentUnit.wingId,
            floorId: currentUnit.floorId,
            unitNumberId: opt.value,
            unitNumber: opt.label
        }));

        setFormData(prev => ({
            ...prev,
            units: [...prev.units, ...newUnits]
        }));

        // Reset only unit numbers so they can quickly add more floors in the same wing
        setCurrentUnit(prev => ({ ...prev, unitNumberIds: [] }));
    };

    const removeUnit = (index) => {
        setFormData(prev => ({
            ...prev,
            units: prev.units.filter((_, i) => i !== index)
        }));
    };

    const validateForm = () => {
        if (!formData.siteId) return toast.warning("Site is required"), false;
        if (!formData.unitName || !formData.unitName.trim()) return toast.warning("Unit Group Name is required"), false;
        if (!formData.isActive) return toast.warning("Status is required"), false;

        if (currentUnit.wingId && currentUnit.floorId && currentUnit.unitNumberIds.length > 0) {
            toast.warning("You have an unadded unit selection. Please click '+ Add' first.");
            return false;
        }

        if (formData.units.length === 0) {
            toast.warning("Please add at least one unit to the list.");
            return false;
        }

        return true;
    };

    const handleSubmit = async () => {
        if (!validateForm()) return;
        setIsLoading(true);

        const payloadObj = {
            unitId: isEdit ? editId : 0,
            siteId: formData.siteId,
            floorId: formData.units.length > 0 ? formData.units[0].floorId : 0,
            wingId: formData.units.length > 0 ? formData.units[0].wingId : 0,
            accessId: 0,
            unitName: formData.unitName,
            unitLogo: "",
            createdBy: "SYS",
            isActive: formData.isActive === 'Active' ? 1 : 0,
            isStaffApprovalRequired: formData.isStaffApprovalRequired,
            units: formData.units.map(u => ({
                wingId: u.wingId,
                floorId: u.floorId,
                unitNumberId: u.unitNumberId || 0,
                unitNumber: u.unitNumber
            }))
        };
        const payload = [payloadObj];

        try {
            const url = isEdit ? `UnitMaster/UpdateExistingUnitMaster/${editId}` : `UnitMaster/AddNewUnitMaster`;
            const method = isEdit ? 'PUT' : 'POST';

            const response = await apiServices({ method, url, data: payload });

            if (response.success && (response.data?.IsSuccess || response.data?.isSuccess || response.data?.success || response.IsSuccess)) {
                toast.success(response.data?.Message || response.data?.message || `Unit ${isEdit ? 'updated' : 'created'} successfully!`);
                navigate('/zentcomm/site-management/unit');
            } else {
                toast.error(response.data?.Message || response.data?.message || response.message || `Failed to ${isEdit ? 'update' : 'create'} unit.`);
            }
        } catch (error) {
            console.error("Submission error:", error);
            toast.error("An error occurred during submission.");
        } finally {
            setIsLoading(false);
        }
    };

    if (isFetching) {
        return (
            <div className="flex-1 flex justify-center items-center h-full">
                <FontAwesomeIcon icon={faSpinner} className="animate-spin text-4xl text-gray-300" />
            </div>
        );
    }

    const rowWings = wings
        .filter(w => !formData.siteId || (w.SiteId || w.siteId) == formData.siteId)
        .map(w => ({ label: w.WingName || w.wingName, value: w.Id || w.id || w.WingId }));

    const rowFloors = floors
        .filter(f => !currentUnit.wingId || (f.WingId || f.wingId) == currentUnit.wingId)
        .map(f => ({ label: f.FloorName || f.floorName || f.FloorNumber || f.floorNumber, value: f.Id || f.id || f.FloorId }));

    const rowUnitNumbers = unitNumbers
        .filter(u => (!currentUnit.wingId || u.WingId == currentUnit.wingId) && (!currentUnit.floorId || u.FloorId == currentUnit.floorId))
        .map(u => ({ label: u.UnitNumber || u.unitNumber, value: u.UnitNumberId || u.Id || u.id }));

    const getWingName = (id) => rowWings.find(w => w.value === id)?.label || 'Unknown Wing';
    const getFloorName = (id) => floors.find(f => (f.Id || f.id || f.FloorId) === id)?.FloorName || 'Unknown Floor';

    return (
        <div className="bg-dashboardbg px-6 py-4 border border-bordercolor flex flex-col rounded-lg flex-1 overflow-y-auto pt-4">
            <Breadcrumbs />
            <PageHeadingSection
                Navbar={true}
                mainHeading={`${isEdit ? 'Update' : 'Add'} Unit`}
                subHeading={`Please fill out the form below to ${isEdit ? 'update the existing' : 'create a new'} unit.`}
            />

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-4">
                <CustomSelect
                    label={<>Site Name <span className="text-red-500">*</span></>}
                    options={sites}
                    value={sites.find(s => s.value === formData.siteId) || null}
                    onChange={(opt) => handleSelectChange('siteId', opt)}
                />

                <div>
                    <label className="block text-xs font-bold text-gray-700 dark:text-textsecondary mb-1" htmlFor="unitName">
                        Unit Group Name <span className="text-red-500">*</span>
                    </label>
                    <input
                        className="w-full px-3 py-2 border border-gray-200 dark:border-bordercolor rounded-md text-sm outline-none bg-transparent text-textprimary placeholder:text-gray-400 focus:border-gray-400 dark:focus:border-gray-500 transition-colors"
                        type="text"
                        id="unitName"
                        value={formData.unitName}
                        onChange={handleInputChange}
                        placeholder="e.g., Godrej or IBM"
                    />
                </div>

                <CustomSelect
                    label={<>Status <span className="text-red-500">*</span></>}
                    options={[{ label: 'Active', value: 'Active' }, { label: 'Inactive', value: 'Inactive' }]}
                    value={formData.isActive ? { label: formData.isActive, value: formData.isActive } : null}
                    onChange={(opt) => handleSelectChange('isActive', opt)}
                />
            </div>

            <div className="mt-6 p-4 bg-orange-50 border border-orange-200 rounded-lg">
                <label className="flex items-center gap-3 cursor-pointer">
                    <div className="relative">
                        <input
                            type="checkbox"
                            className="sr-only"
                            checked={formData.isStaffApprovalRequired}
                            onChange={(e) => {
                                const isChecked = e.target.checked;
                                if (isChecked) {
                                    const confirm = window.confirm("WARNING: Enabling this will strictly require Unit Admin approval for all vendor staff assigned to this unit. Do you want to proceed?");
                                    if (confirm) {
                                        setFormData(prev => ({ ...prev, isStaffApprovalRequired: true }));
                                    }
                                } else {
                                    setFormData(prev => ({ ...prev, isStaffApprovalRequired: false }));
                                }
                            }}
                        />
                        <div className={`block w-10 h-6 rounded-full transition-colors ${formData.isStaffApprovalRequired ? 'bg-orange-500' : 'bg-gray-300'}`}></div>
                        <div className={`absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition-transform ${formData.isStaffApprovalRequired ? 'transform translate-x-4' : ''}`}></div>
                    </div>
                    <div>
                        <div className="text-xs font-bold text-gray-800">Strict Staff Approval (Policy Engine)</div>
                        <div className="text-[10px] text-gray-600">If enabled, staff cannot access this unit without explicit approval from a Unit Admin.</div>
                    </div>
                </label>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6 pt-6 border-t border-gray-200 dark:border-bordercolor">
                <CustomSelect
                    label={<>Wing <span className="text-red-500">*</span></>}
                    options={rowWings}
                    value={rowWings.find(w => w.value === currentUnit.wingId) || null}
                    onChange={(opt) => handleCurrentUnitChange('wingId', opt)}
                    disabled={!formData.siteId}
                />

                <CustomSelect
                    label={<>Floor <span className="text-red-500">*</span></>}
                    options={rowFloors}
                    value={rowFloors.find(f => f.value === currentUnit.floorId) || null}
                    onChange={(opt) => handleCurrentUnitChange('floorId', opt)}
                    disabled={!currentUnit.wingId}
                />

                <div className="flex items-end gap-3">
                    <div className="flex-1">
                        <CustomSelect
                            label={<>Unit Numbers <span className="text-red-500">*</span></>}
                            options={rowUnitNumbers}
                            value={currentUnit.unitNumberIds}
                            onChange={(opts) => handleCurrentUnitChange('unitNumberIds', opts)}
                            disabled={!currentUnit.floorId}
                            placeholder="Select Unit Nos"
                            isMulti={true}
                        />
                    </div>

                    <button
                        onClick={handleAddUnitToList}
                        className="h-[38px] px-4 mb-[1px] bg-blue-50 hover:bg-blue-100 text-blue-600 border border-blue-200 rounded-md text-xs font-bold flex items-center gap-2 transition-all shrink-0"
                    >
                        <FontAwesomeIcon icon={faPlus} />
                        Add
                    </button>
                </div>
            </div>

            {/* List Below */}
            {formData.units.length > 0 && (
                <div className="mt-8">
                    <h3 className="text-xs font-bold text-gray-700 dark:text-textprimary mb-3 uppercase tracking-wider">
                        Added Units ({formData.units.length})
                    </h3>
                    <div className="flex flex-wrap gap-2.5">
                        {formData.units.map((unit, index) => (
                            <div key={index} className="flex items-center gap-3 px-3 py-1.5 bg-gray-50 dark:bg-actionitembg border border-gray-200 dark:border-bordercolor rounded-full shadow-sm group">
                                <span className="text-xs text-gray-700 dark:text-textprimary font-medium">
                                    {getWingName(unit.wingId)} <span className="text-gray-400 mx-1">•</span>
                                    {getFloorName(unit.floorId)} <span className="text-gray-400 mx-1">•</span>
                                    <span className="font-bold text-blue-600 dark:text-blue-400">{unit.unitNumber}</span>
                                </span>
                                <button
                                    onClick={() => removeUnit(index)}
                                    className="text-gray-400 hover:text-red-500 transition-colors"
                                    title="Remove Unit"
                                >
                                    <FontAwesomeIcon icon={faTrash} className="text-[10px]" />
                                </button>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            <div className="flex justify-between mt-auto border-t pt-4">
                <button
                    onClick={() => navigate("/zentcomm/site-management/unit")}
                    className="px-3 py-2 border dark:border-bordercolor rounded-md text-xs flex items-center gap-2 text-gray-600 dark:text-textprimary hover:bg-gray-50 dark:hover:bg-actionitembg transition-colors"
                >
                    <FontAwesomeIcon icon={faAngleLeft} />
                    Cancel
                </button>

                <button
                    onClick={handleSubmit}
                    disabled={isLoading}
                    className="px-4 py-2 rounded-md text-xs font-bold flex items-center gap-2 shadow-sm hover:shadow-md active:scale-95 transition-all duration-200"
                    style={{ backgroundColor: secondaryColor(), color: primaryColor() }}
                    onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = primaryColor();
                        e.currentTarget.style.color = '#FFFFFF';
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = secondaryColor();
                        e.currentTarget.style.color = primaryColor();
                    }}
                >
                    {isLoading ? <FontAwesomeIcon icon={faSpinner} className="animate-spin" /> : (isEdit ? 'Update' : 'Submit')}
                    <FontAwesomeIcon icon={faAngleRight} />
                </button>
            </div>
        </div>
    );
};

export default UnitForm;
